import { assetUrl, http, isOfflineError, orUndefined, storeImage } from "./http";
import {
  enqueue,
  newActionKey,
  provisionalNote,
  provisionalRef,
  registerProcessor,
  replayOptions,
  type QueuedOffline,
} from "@/offline/outbox";
import type { ReceiptData } from "@/utils/receipt";
import { receiptBody, toBalance, type ApiBalance, type ReceiptInput } from "./paymentService";
import type {
  Enrollment,
  EnrollmentBalance,
  EnrollmentStatus,
  Guardian,
  GuardianRelation,
  Sexe,
  Student,
  StudentGuardian,
} from "@/types";

/* -------------------------------------------------------------------------- */
/* Format du backend                                                           */
/* -------------------------------------------------------------------------- */

interface ApiStudent {
  id: number;
  matricule: string;
  firstName: string;
  lastName: string;
  gender: Sexe;
  birthDate: string;
  birthPlace: string | null;
  address: string | null;
  phone: string | null;
  photoUrl: string | null;
  notes: string | null;
}

interface ApiGuardian {
  id: number;
  firstName: string;
  lastName: string;
  phone: string;
  address: string | null;
  profession: string | null;
}

interface ApiLink {
  studentId: number;
  guardianId: number;
  relation: GuardianRelation;
  isPrimary: boolean;
}

interface ApiEnrollment {
  id: number;
  studentId: number;
  schoolYear: string;
  classId: number;
  cycleId: number;
  enrollmentDate: string;
  status: EnrollmentStatus;
  leftAt?: string | null;
  leftReason?: string | null;
}

const toStudent = (s: ApiStudent): Student => ({
  id: String(s.id),
  matricule: s.matricule,
  firstName: s.firstName,
  lastName: s.lastName,
  gender: s.gender,
  birthDate: s.birthDate,
  birthPlace: s.birthPlace ?? "",
  address: s.address ?? "",
  phone: s.phone ?? "",
  photo: assetUrl(s.photoUrl),
  notes: s.notes ?? "",
});

const toGuardian = (g: ApiGuardian): Guardian => ({
  id: String(g.id),
  firstName: g.firstName,
  lastName: g.lastName,
  phone: g.phone,
  address: g.address ?? "",
  profession: g.profession ?? "",
});

const toEnrollment = (e: ApiEnrollment): Enrollment => ({
  id: String(e.id),
  studentId: String(e.studentId),
  schoolYear: e.schoolYear,
  classId: String(e.classId),
  cycleId: String(e.cycleId),
  enrollmentDate: e.enrollmentDate,
  status: e.status,
  leftAt: e.leftAt ?? undefined,
  leftReason: e.leftReason ?? undefined,
});

/** Corps envoyé au backend pour un élève (les champs vides ne sont pas envoyés). */
async function studentBody(s: Partial<Student>) {
  return {
    firstName: s.firstName?.trim(),
    lastName: s.lastName?.trim(),
    gender: s.gender,
    birthDate: orUndefined(s.birthDate),
    birthPlace: orUndefined(s.birthPlace),
    address: orUndefined(s.address),
    phone: orUndefined(s.phone),
    photoUrl: await storeImage(s.photo),
    notes: orUndefined(s.notes),
  };
}

const guardianBody = (g: Omit<Guardian, "id">) => ({
  firstName: g.firstName.trim(),
  lastName: g.lastName.trim(),
  phone: g.phone.trim(),
  address: orUndefined(g.address),
  profession: orUndefined(g.profession),
});

/* -------------------------------------------------------------------------- */
/* Élèves                                                                      */
/* -------------------------------------------------------------------------- */

export async function getStudents(): Promise<Student[]> {
  return (await http.getAll<ApiStudent>("/students")).map(toStudent);
}

/** Crée un dossier élève permanent (réutilisable sur plusieurs inscriptions). Le backend attribue le matricule. */
export async function createStudent(
  payload: Omit<Student, "id" | "matricule"> & { matricule?: string },
): Promise<Student> {
  return toStudent(await http.post<ApiStudent>("/students", await studentBody(payload)));
}

export async function updateStudent(id: string, patch: Partial<Student>): Promise<Student> {
  return toStudent(await http.put<ApiStudent>(`/students/${id}`, await studentBody(patch)));
}

/** L'élève quitte l'école (date, motif) : son inscription de l'année passe « A quitté » ; il sort des listes de classe. */
export async function leaveSchool(enrollmentId: string, date: string, reason: string): Promise<Enrollment> {
  return toEnrollment(await http.post<ApiEnrollment>(`/enrollments/${enrollmentId}/leave`, { date, reason: reason.trim() }));
}

/** Annule un départ déclaré par erreur (administrateur). */
export async function rejoinSchool(enrollmentId: string): Promise<Enrollment> {
  return toEnrollment(await http.post<ApiEnrollment>(`/enrollments/${enrollmentId}/rejoin`, {}));
}

/* -------------------------------------------------------------------------- */
/* Responsables (parents)                                                      */
/* -------------------------------------------------------------------------- */

export async function getGuardians(): Promise<Guardian[]> {
  return (await http.getAll<ApiGuardian>("/guardians")).map(toGuardian);
}

export async function createGuardian(payload: Omit<Guardian, "id">): Promise<Guardian> {
  return toGuardian(await http.post<ApiGuardian>("/guardians", guardianBody(payload)));
}

export async function updateGuardian(id: string, payload: Omit<Guardian, "id">): Promise<Guardian> {
  return toGuardian(await http.put<ApiGuardian>(`/guardians/${id}`, guardianBody(payload)));
}

/** Refusé (409) tant que le responsable est lié à un élève. */
export async function deleteGuardian(id: string): Promise<void> {
  await http.delete(`/guardians/${id}`);
}

/** Modifie le lien (lien de parenté, responsable principal). */
export async function updateGuardianLink(link: StudentGuardian): Promise<void> {
  await http.patch(`/students/${link.studentId}/guardians/${link.guardianId}`, {
    relation: link.relation,
    isPrimary: link.isPrimary,
  });
}

/** Délie un enfant de son responsable (refusé s'il ne lui reste aucun autre responsable). */
export async function unlinkGuardian(studentId: string, guardianId: string): Promise<void> {
  await http.delete(`/students/${studentId}/guardians/${guardianId}`);
}

export async function getStudentGuardians(): Promise<StudentGuardian[]> {
  const list = await http.get<ApiLink[]>("/student-guardians");
  return list.map((l) => ({ ...l, studentId: String(l.studentId), guardianId: String(l.guardianId) }));
}

export async function linkGuardian(link: StudentGuardian): Promise<StudentGuardian> {
  await http.post(`/students/${link.studentId}/guardians`, {
    guardianId: Number(link.guardianId),
    relation: link.relation,
    isPrimary: link.isPrimary,
  });
  return link;
}

/* -------------------------------------------------------------------------- */
/* Inscriptions                                                                */
/* -------------------------------------------------------------------------- */

export async function getEnrollments(): Promise<Enrollment[]> {
  return (await http.getAll<ApiEnrollment>("/enrollments")).map(toEnrollment);
}

/** Responsable à rattacher lors d'une inscription : existant (id) ou nouveau dossier. */
export type RegistrationGuardian =
  | { guardianId: string; relation: GuardianRelation; isPrimary: boolean }
  | { guardian: Omit<Guardian, "id">; relation: GuardianRelation; isPrimary: boolean };

/**
 * Inscription complète en UN appel (élève + responsables + inscription), tout ou rien.
 * - `student` : nouvel élève (au moins un responsable) ;
 * - `existingStudentId` : réinscription d'un élève existant (nouvelle ligne, historique conservé).
 */
export interface RegistrationPayload {
  student?: Omit<Student, "id" | "matricule">;
  existingStudentId?: string;
  guardians: RegistrationGuardian[];
  enrollment: { classId: string; schoolYear: string; enrollmentDate: string; status: EnrollmentStatus };
  /** Versement au guichet à l'inscription (optionnel) : un reçu, dans la même transaction. */
  receipt?: ReceiptInput;
}

/**
 * Hors ligne (avec `offline`) : l'inscription, photo comprise, est gardée sur l'appareil et envoyée au retour
 * du réseau, avec un reçu provisoire s'il y a un versement. Un élève de même nom et même date de naissance
 * trouvé à l'envoi la met « à vérifier » au lieu de la créer.
 */
export async function registerEnrollment(
  payload: RegistrationPayload,
  offline?: { label: string; provisional?: (ref: string) => ReceiptData },
): Promise<{ student: Student; enrollment: Enrollment; receiptNo: string | null } | QueuedOffline> {
  const key = newActionKey();
  try {
    return await submitEnrollment(payload, { idempotencyKey: key });
  } catch (error) {
    if (!offline || !isOfflineError(error)) throw error;
    const ref = provisionalRef(key);
    const provisional = (payload.receipt && offline.provisional?.(ref)) || null;
    await enqueue({
      id: key,
      kind: "enrollment",
      label: offline.label,
      payload: payload.receipt
        ? { ...payload, receipt: { ...payload.receipt, note: provisionalNote(payload.receipt.note, ref) } }
        : payload,
      provisional: provisional ?? undefined,
    });
    return { queued: true, provisional };
  }
}

const sameName = (value: string | undefined) =>
  (value ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

registerProcessor("enrollment", async (item) => {
  const payload = item.payload as RegistrationPayload;
  const s = payload.student;
  if (s && !item.force) {
    const twin = (await getStudents()).find(
      (other) =>
        sameName(other.lastName) === sameName(s.lastName) &&
        sameName(other.firstName) === sameName(s.firstName) &&
        (other.birthDate ?? "") === (s.birthDate ?? ""),
    );
    if (twin) {
      return {
        done: false,
        review: `Un élève ${twin.lastName} ${twin.firstName} (${twin.matricule}) né le même jour existe déjà. Créez quand même s'il s'agit d'un autre élève, sinon supprimez cette inscription.`,
      };
    }
  }
  await submitEnrollment(payload, replayOptions(item));
  return { done: true };
});

/** Envoi de l'inscription ; null si sa clé d'envoi était déjà enregistrée (renvoi après coupure). */
async function submitEnrollment(
  payload: RegistrationPayload,
  send: { idempotencyKey: string; replay?: boolean },
): Promise<{ student: Student; enrollment: Enrollment; receiptNo: string | null } | null> {
  const enrollmentBody = {
    classId: Number(payload.enrollment.classId),
    schoolYear: orUndefined(payload.enrollment.schoolYear),
    enrollmentDate: orUndefined(payload.enrollment.enrollmentDate),
  };
  const receipt = payload.receipt?.lines.length ? receiptBody(payload.receipt) : undefined;
  type Registered = { student: ApiStudent; enrollment: ApiEnrollment; receipt: { receiptNo: string } | null };
  let result: Registered | null;
  if (payload.existingStudentId) {
    result = await http.post<Registered | null>(
      "/enrollments/reenroll",
      { studentId: Number(payload.existingStudentId), ...enrollmentBody, receipt },
      send,
    );
  } else {
    result = await http.post<Registered | null>(
      "/enrollments",
      {
        student: payload.student ? await studentBody(payload.student) : undefined,
        guardians: payload.guardians.map((g) =>
          "guardianId" in g
            ? { guardianId: Number(g.guardianId), relation: g.relation, isPrimary: g.isPrimary }
            : { guardian: guardianBody(g.guardian), relation: g.relation, isPrimary: g.isPrimary },
        ),
        enrollment: enrollmentBody,
        receipt,
      },
      send,
    );
  }
  if (!result) return null;
  // Le backend crée l'inscription « active » : un autre statut choisi est appliqué ensuite.
  if (payload.enrollment.status !== result.enrollment.status) {
    result.enrollment = await http.patch<ApiEnrollment>(`/enrollments/${result.enrollment.id}`, {
      status: payload.enrollment.status,
    });
  }
  return {
    student: toStudent(result.student),
    enrollment: toEnrollment(result.enrollment),
    receiptNo: result.receipt?.receiptNo ?? null,
  };
}

/* -------------------------------------------------------------------------- */
/* Dossier complet d'un élève (un seul appel)                                  */
/* -------------------------------------------------------------------------- */

export interface StudentDossier {
  student: Student;
  guardians: Array<{ guardian: Guardian; link: StudentGuardian }>;
  /** Inscriptions de la plus récente à la plus ancienne, chacune avec sa situation des frais. */
  enrollments: Array<{ enrollment: Enrollment; balance: EnrollmentBalance }>;
  /** Totaux sur les inscriptions non annulées. */
  summary: { enrollmentsCount: number; totalDue: number; totalPaid: number; totalRemaining: number };
}

interface ApiDossier {
  student: ApiStudent;
  guardians: Array<{ guardian: ApiGuardian; relation: GuardianRelation; isPrimary: boolean }>;
  enrollments: Array<{ enrollment: ApiEnrollment; balance: ApiBalance }>;
  summary: StudentDossier["summary"];
}

export async function getStudentDossier(studentId: string): Promise<StudentDossier> {
  const d = await http.get<ApiDossier>(`/students/${studentId}/dossier`);
  return {
    student: toStudent(d.student),
    guardians: d.guardians.map((g) => ({
      guardian: toGuardian(g.guardian),
      link: { studentId: String(d.student.id), guardianId: String(g.guardian.id), relation: g.relation, isPrimary: g.isPrimary },
    })),
    enrollments: d.enrollments
      .map((e) => ({ enrollment: toEnrollment(e.enrollment), balance: toBalance(e.balance) }))
      .sort((a, b) => b.enrollment.schoolYear.localeCompare(a.enrollment.schoolYear)),
    summary: d.summary,
  };
}
